import React, { useState, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { NavLink, useRouteMatch, Route, Switch, useParams, useHistory, useLocation } from "react-router-dom";

import cx from 'classnames';
import _ from 'lodash';
import {
  Row,
  Col,
  ListGroup,
  Button,
  Image,
  OverlayTrigger,
  Popover,
  Tooltip
} from "react-bootstrap";

import StoryboardPermissions from './permissions';
import MediaLibrary from './mediaLibrary';
import LeadsView from "./leadReports";
import LeadProfile from "./leadReports/leadProfile";
import ReportsView from './reports/';
import { ContentManagerSettings } from "./contentManager";
import LandingPageReportsView from './reports/landingPage';
import WebsiteSettings from './websiteSettings';
import CustomFieldsView from './customFields';
import PerformanceView from './sitePerformance';
import PersonalizationCampaign from "./personalizationCampaign";

import { fetchStoryboard, invalidateStoryboardState } from "actions/storyboard";
import storyboardRoleService from 'services/storyboardRoles.service';
import { getBaseUrl } from 'services/util';
import JourneyContainer from "./journey";
import Personalization from "./personalization";
import TruncatedTextWithTooltip from "components/TruncatedTextWithTooltip";
import { ReportsProvider } from "./reports/ReportsProvider";

import styles from './storyboardSettings.module.scss';

export default function StoryboardsSettings() {
  const dispatch = useDispatch();
  const history = useHistory();

  let { path, url } = useRouteMatch();
  let baseUrl = getBaseUrl(url);
  const location = useLocation();

  let { storyboardId } = useParams();
  const [selectedDomain, setSelectedDomain] = useState({});
  const storyboard = useSelector((state) => _.get(state, "storyboards.storyboard.item", null));
  const user = useSelector((state) => _.get(state, "users.me"));
  const storyboardType = _.get(storyboard, 'type');
  const storyboardList = useSelector((state) => _.get(state, "storyboardList.item", null));
  var domain = useSelector(state => _.get(state, 'workspaces.workspace.item.defaultDomain') || "");
  const canonical = _.get(storyboard, 'page.canonical') || "";
  const liveUrl = `${_.get(selectedDomain, 'url')}${canonical}`;
  const published = _.get(storyboard, 'page.published');
  const changes = _.get(storyboard, 'page.changes');
  const isJourneyRoute = location.pathname.includes('/journey');

  useEffect(() => {
    dispatch(fetchStoryboard(storyboardId));
  }, [storyboardId]);

  useEffect(()=>{
    let domainUrl = _.get(storyboard,'domain') || domain;
    setSelectedDomain(domainUrl);
  },[storyboard, domain])

  useEffect(() => {
    return () => {
      dispatch(invalidateStoryboardState());
    };
  }, [])

  if (!storyboard) {
    return (
      <div className={styles.loaderContainer}>
        <label>Loading Storyboard...</label>
      </div>
    )
  }

  const handleBackClick = () => {
    var url = `${baseUrl}/storyboards`;

    if (storyboardList && storyboardList.id) {
      url = `${baseUrl}/storyboards/folder/${storyboardList.id}`;
    }

    history.push(url);
  }

  const handleLiveSiteClicked = () => {
    if (canonical) {
      const win = window.open(liveUrl, "_blank");
      win.focus();
    }
  }

  const handlePreviewPage = () => {
    var page = storyboard.page;
    if (!page) {
      return;
    }

    const url = `${baseUrl}/pages/${page.id}/preview`;
    const win = window.open(url, "_blank");
    win.focus();
  }

  const handleEditClick = () => {
    if (!storyboardRoleService.canEditStoryboard(storyboard)) {
      return;
    }

    var page = storyboard.page;

    if (page) {
      const win = window.open(`${baseUrl}/pages/${page.id}`, "_blank");
      win.focus();
    }
  };

  const viewerModePopover = (
    <Popover id="popover-basic" className={cx(styles.viewerMessage, { [styles.hideMessage]: storyboardRoleService.canEditStoryboard(storyboard) })}>
      <Popover.Header>
        <span className={styles.roleInfo}>Page role: <span className={styles.roleLabel}>{storyboard.role}</span></span>
      </Popover.Header>
      <Popover.Body>
        You don't have permission to edit this site. Contact the owner for the access.
      </Popover.Body>
    </Popover>
  );

  return (
    <div className={styles.content}>
      <Row className={styles.storyboardsWrapper}>
        <Col className={styles.settingsTitleHeader} md={12}>
          <div className={styles.customRow}>
            <div className={styles.backButtonContainer}>
              <Button variant="light" onClick={handleBackClick} className={styles.backButton}>
                <i className={cx('cleverstory-back')} />
              </Button>
            </div>
            <div className={styles.titleRow}>
              <TruncatedTextWithTooltip placement="bottom" maxWidth={400} content={storyboard.name}>
                <h6>{storyboard.name}</h6>
              </TruncatedTextWithTooltip>
              <span className={styles.viewUrl} onClick={handleLiveSiteClicked}>
                {published &&
                  <>
                    <label className={cx(styles.status, styles.live)}>LIVE</label>
                    <OverlayTrigger key={'live Url'} placement={'top'} trigger={'hover'} overlay={<Tooltip>{liveUrl}</Tooltip>}>
                      <i className={cx(styles.icon, "cleverstory-url")} />
                    </OverlayTrigger>
                  </>
                }
              </span>
            </div>
          </div>
          <div className={styles.rolesAndUrlsRow}>
            <Button variant="transparent" className={styles.rolesAndUrls} onClick={handlePreviewPage} data-track-event="Mixpanel" data-track-props="Preview from Settings">
              <i className={cx('cleverstory-view')} />
              <span className={styles.previewText}>Preview</span>
            </Button>
            <OverlayTrigger trigger="hover focus" placement="bottom" overlay={viewerModePopover}>
              <Button variant="transparent" className={styles.rolesAndUrls} disabled={!storyboardRoleService.canEditStoryboard(storyboard)} onClick={handleEditClick} data-track-event="Mixpanel" data-track-props="Edit from Settings">
                <i className={cx('cleverstory-edit')} />
                <span className={styles.editSite}>Edit Site</span>
              </Button>
            </OverlayTrigger>
          </div>
        </Col>
        <Col className={styles.settingsMenu} md={3}>

          <ListGroup variant="flush" className={styles.settingsListGroup}>

            <OverlayTrigger key={'site_settings'} placement={'right'} trigger={'hover'} overlay={<Tooltip>Site Settings</Tooltip>}>
              <ListGroup.Item as={NavLink} activeClassName={styles.active} to={`${url}/website-settings`}>
                <i className={cx('cleverstory-story-settings', styles.storySettingsIcon)} />
              </ListGroup.Item>
            </OverlayTrigger>

            {storyboardRoleService.canManageStoryboard(storyboard) &&
              <OverlayTrigger key={'right'} placement={'right'} trigger={'hover'} overlay={<Tooltip>Roles & Permissions</Tooltip>}>
                <ListGroup.Item as={NavLink} activeClassName={styles.active} to={`${url}/permissions`}>
                  <i className={cx('cleverstory-user-role')} />
                </ListGroup.Item>
              </OverlayTrigger>
            }

            {/* {storyboardRoleService.canEditStoryboard(storyboard) &&
              <ListGroup.Item as={NavLink} activeClassName={styles.active} to={`${url}/media-library`}>
                Media Library
              </ListGroup.Item>
            } */}

            <OverlayTrigger placement={'right'} trigger={'hover'} overlay={<Tooltip>Reports</Tooltip>}>
              <ListGroup.Item as={NavLink} activeClassName={styles.active} to={`${url}/reports`}>
                <i className={cx('cleverstory-trendline')} />
              </ListGroup.Item>
            </OverlayTrigger>

            <OverlayTrigger placement={'right'} trigger={'hover'} overlay={<Tooltip>Lead Reports</Tooltip>}>
              <ListGroup.Item as={NavLink} activeClassName={styles.active} to={`${url}/leads`}>
                <i className={cx('cleverstory-lead-analytics')} />
              </ListGroup.Item>
            </OverlayTrigger>

            {storyboardType === 'RESOURCE_CENTER' &&
              <OverlayTrigger placement={'right'} trigger={'hover'} overlay={<Tooltip>Content Manager</Tooltip>}>
                <ListGroup.Item as={NavLink} activeClassName={styles.active} to={`${url}/content-manager`}>
                  <i className={cx('cleverstory-content-manager')} />
                </ListGroup.Item>
              </OverlayTrigger>
            }

            {storyboardType === 'RESOURCE_CENTER' &&
              <OverlayTrigger placement={'right'} trigger={'hover'} overlay={<Tooltip>Custom Fields</Tooltip>}>
                <ListGroup.Item as={NavLink} activeClassName={styles.active} to={`${url}/custom-fields`}>
                  <i className={cx('cleverstory-custom-fields')} />
                </ListGroup.Item>
              </OverlayTrigger>
            }
            {storyboardType != 'SPATIAL' &&
              <OverlayTrigger placement={'right'} trigger={'hover'} overlay={<Tooltip>Journey</Tooltip>}>
                <ListGroup.Item as={NavLink} activeClassName={styles.active} to={`${url}/journey`}>
                  <i className={cx('cleverstory-journey')} />
                </ListGroup.Item>
              </OverlayTrigger>
            }

            <OverlayTrigger placement={'right'} trigger={'hover'} overlay={<Tooltip>Performance</Tooltip>}>
              <ListGroup.Item as={NavLink} activeClassName={styles.active} to={`${url}/performance`}>
                <i className={cx('cleverstory-degree')} />
              </ListGroup.Item>
            </OverlayTrigger>

            <OverlayTrigger placement={'right'} trigger={'hover'} overlay={<Tooltip>ABM & Distribution</Tooltip>}>
              <ListGroup.Item as={NavLink} activeClassName={styles.active} to={`${url}/personalization`}>
                <i className={cx('cleverstory-personalized-link')} />
              </ListGroup.Item>
            </OverlayTrigger>

            <OverlayTrigger placement={'right'} trigger={'hover'} overlay={<Tooltip>ABM & Campaign</Tooltip>}>
              <ListGroup.Item as={NavLink} activeClassName={styles.active} to={`${url}/personalization-campaign`}>
                <i className={cx('cleverstory-personalization-campaign')} />
              </ListGroup.Item>
            </OverlayTrigger>

          </ListGroup>
        </Col>
        <Col className={cx(styles.settingsContent, { [styles.journeyContent]: isJourneyRoute })} md={9}>
          <Switch>
            <Route path={`${path}/website-settings`}>
              <WebsiteSettings />
            </Route>

            {storyboardRoleService.canManageStoryboard(storyboard) &&
              <Route path={`${path}/permissions`}>
                <StoryboardPermissions />
              </Route>
            }

            {storyboardType === 'RESOURCE_CENTER' && storyboardRoleService.canEditStoryboard(storyboard) &&
              <Route path={`${path}/custom-fields`}>
                <CustomFieldsView />
              </Route>
            }

            {storyboardRoleService.canEditStoryboard(storyboard) &&
              <Route path={`${path}/media-library`}>
                <MediaLibrary />
              </Route>
            }

            <Route  path={`${path}/content-manager`}>
              <ContentManagerSettings storyboard={storyboard}/>
            </Route>

            <Route exact path={`${path}/leads`}>
              <LeadsView />
            </Route>

            <Route path={`${path}/leads/profile`}>
              <LeadProfile />
            </Route>

            <Route path={`${path}/reports`} component={ReportsWrapper} />

            <Route path={`${path}/journey`}>
              <JourneyContainer/>
            </Route>

            <Route  path={`${path}/performance`}>
              <PerformanceView storyboard={storyboard}/>
            </Route>

            <Route path={`${path}/personalization`}>
              <Personalization/>
            </Route>

            <Route path={`${path}/personalization-campaign`}>
              <PersonalizationCampaign/>
            </Route>

            <Route component={AccessDenied} />
          </Switch>
        </Col>
      </Row>
    </div>
  )
}

function AccessDenied() {

  const location = useLocation();
  const paths = location.pathname.split('/');
  const pathName = paths[(paths.length - 1)];

  const messages = [
    {
      name: 'media-library',
      title: 'Do you believe in black holes?',
      discription: 'Today’s weather forecast: Clear skies and empty screens',
      image: 'url'
    },
    {
      name: 'website-settings',
      title: 'Do you believe in black holes?',
      discription: 'You better do, because nothing else explains this empty screen....That or you haven’t added anything here yet.',
      image: 'url'
    },
    {
      name: 'reports',
      title: 'Do you believe in black holes?',
      discription: 'Satellite images of this screen show a blank slate - zero, nothing, nada.',
      image: 'url'
    }
  ]

  var message = messages.find(item => {
    return pathName === item.name;
  })

  if (!message) {
    message = messages[0];
  }

  // const message = messages[`${pathName}`];
  if (message.name === 'media-library') {
    return <CustomFieldsView hideButtons={true}/>;
  }

  return (
    <div className={styles.errorMessageContainer}>
      <div className={styles.errorMessageInner}>
        <Image src={`${process.env.REACT_APP_UI_URL}/images/userNotFound.svg`} className={styles.errorMessageImage} />
        <p>{message.discription}</p>
      </div>
    </div>
  )
}

const ReportsWrapper = () => {
  const storyboard = useSelector((state) => _.get(state, "storyboards.storyboard.item", null));
  const storyboardType = _.get(storyboard, 'type');

  return (
    <ReportsProvider>
      {storyboardType === 'INTERACTIVE_CONTENT' ? <ReportsView /> : <LandingPageReportsView />}
    </ReportsProvider>
  )
};